import React from 'react';
import { Box, Dialog, Stack, Typography } from '@mui/material';

import { Button } from '../../../../components/core';
import { WebhookEndpoint, WebhookEventDataType } from '../../types/types';
import { getEventDeliveryDiffs } from '../../utils/eventDeliveryDiff';

type WebhookEventRetryModalProps = {
  open: boolean;
  event: WebhookEventDataType;
  subscription: WebhookEndpoint;
  isRetrying: boolean;
  onClose: () => void;
  onRetryWithSavedSettings: () => void;
  onRetryWithCurrentSubscription: () => void;
};

const WebhookEventRetryModal = ({
  open,
  event,
  subscription,
  isRetrying,
  onClose,
  onRetryWithSavedSettings,
  onRetryWithCurrentSubscription,
}: WebhookEventRetryModalProps) => {
  const diffs = getEventDeliveryDiffs(event, subscription);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 4,
          minWidth: { xs: '90%', md: '733px' },
          p: 3,
        },
      }}
    >
      <Typography variant="xl" fontWeight="semibold" sx={{ mb: 2 }}>
        Retry webhook event
      </Typography>

      <Typography variant="large" sx={{ mb: 3 }}>
        This event&apos;s original delivery settings differ from the current webhook subscription.
        Choose whether to retry using the original settings or the current subscription settings.
      </Typography>

      <Stack spacing={3} sx={{ mb: 4 }}>
        {diffs.map((diff) => (
          <Box key={diff.field}>
            <Typography variant="medium" fontWeight="semibold" sx={{ mb: 1 }}>
              {diff.label}
            </Typography>
            <Box sx={{ display: 'grid', gap: 1 }}>
              <Box>
                <Typography variant="small" color="text.secondary">
                  Original
                </Typography>
                <Typography variant="medium" sx={{ wordBreak: 'break-all' }}>
                  {diff.eventValue}
                </Typography>
              </Box>
              <Box>
                <Typography variant="small" color="text.secondary">
                  Current subscription
                </Typography>
                <Typography variant="medium" sx={{ wordBreak: 'break-all' }}>
                  {diff.subscriptionValue}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </Stack>

      <Stack spacing={2} sx={{ '& button': { height: '48px' } }}>
        <Button
          text={isRetrying ? 'Retrying...' : 'Retry with current webhook settings'}
          disabled={isRetrying}
          onClick={onRetryWithCurrentSubscription}
          theme="primary"
        />
        <Button
          text={isRetrying ? 'Retrying...' : 'Retry with original settings'}
          disabled={isRetrying}
          onClick={onRetryWithSavedSettings}
          theme="secondary"
        />
        <Button text="Cancel" disabled={isRetrying} onClick={onClose} theme="secondary" />
      </Stack>
    </Dialog>
  );
};

export default WebhookEventRetryModal;
